import { Roles } from "../constants/roles";
import { FormBase } from "../util/form-base-form";

class HorizontalSliderRenderFormBase extends FormBase {
    public getHomeSection(root: EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, ".home-section", HTMLElement);
    }

    public getIcon(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, "i", HTMLElement);
    }

    public getDots(root: ParentNode): HTMLButtonElement[] {
        return this.queryElements(root, `[data-role="${Roles.HORIZONTAL_SLIDER_DOT}"]`, HTMLButtonElement);
    }
}

const horizontalSliderRenderFormBase = new HorizontalSliderRenderFormBase();

type ControlState = {
    atStart: boolean;
    atEnd: boolean;
    noOverflow: boolean;
    canWrap: boolean;
};

export class HorizontalSliderRender {
    public static renderSectionVisibility(root: HTMLElement, hasSlides: boolean): void {
        const section = horizontalSliderRenderFormBase.getHomeSection(root);
        if (!(section instanceof HTMLElement)) return;

        section.hidden = !hasSlides;
        section.setAttribute("aria-hidden", hasSlides ? "false" : "true");
    }

    public static renderControls(
        root: HTMLElement,
        prevButton: HTMLButtonElement,
        nextButton: HTMLButtonElement,
        state: ControlState,
    ): void {
        prevButton.disabled = state.noOverflow || (!state.canWrap && state.atStart);
        nextButton.disabled = state.noOverflow || (!state.canWrap && state.atEnd);
        root.classList.toggle("is-static", state.noOverflow);

        HorizontalSliderRender.renderEdgeIcons(prevButton, nextButton, state);
    }

    public static renderEdgeIcons(
        prevButton: HTMLButtonElement,
        nextButton: HTMLButtonElement,
        state: Pick<ControlState, "atStart" | "atEnd" | "canWrap">,
    ): void {
        const prevIcon = horizontalSliderRenderFormBase.getIcon(prevButton);
        const nextIcon = horizontalSliderRenderFormBase.getIcon(nextButton);
        if (!(prevIcon instanceof HTMLElement) || !(nextIcon instanceof HTMLElement)) return;

        const isPrevScrollBack = state.canWrap && state.atStart;
        const isNextScrollBack = state.canWrap && state.atEnd;

        prevIcon.className = isPrevScrollBack ? "bi bi-chevron-double-right" : "bi bi-chevron-left";
        nextIcon.className = isNextScrollBack ? "bi bi-chevron-double-left" : "bi bi-chevron-right";
    }

    public static ensureDots(dotsContainer: HTMLElement, count: number): void {
        const currentCount = dotsContainer.children.length;
        if (currentCount === count) return;

        dotsContainer.innerHTML = "";
        for (let i = 0; i < count; i += 1) {
            const dot = document.createElement("button");
            dot.type = "button";
            dot.className = "horizontal-slider__dot";
            dot.setAttribute("data-role", Roles.HORIZONTAL_SLIDER_DOT);
            dot.setAttribute("data-index", String(i));
            dot.setAttribute("aria-label", `Go to slide ${i + 1}`);
            dotsContainer.appendChild(dot);
        }
    }

    public static renderActiveDot(dotsContainer: HTMLElement, activeIndex: number): void {
        const dots = horizontalSliderRenderFormBase.getDots(dotsContainer);
        dots.forEach((dot, index) => {
            const isActive = index === activeIndex;
            dot.classList.toggle("is-active", isActive);
            dot.setAttribute("aria-current", isActive ? "true" : "false");
        });
    }

    public static renderDotsInteractivity(dotsContainer: HTMLElement, disabled: boolean): void {
        const dots = horizontalSliderRenderFormBase.getDots(dotsContainer);
        dots.forEach((dot) => {
            dot.disabled = disabled;
            dot.setAttribute("aria-disabled", disabled ? "true" : "false");
            dot.tabIndex = disabled ? -1 : 0;
        });
    }
}
